import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { motion } from 'motion/react';
import {
  CARD_CENTER_INDEX,
  CARD_SPIN_MS_SCALE,
  CARD_TILE_COUNT,
  CLEAR_TRANSITION_MS,
  EXTEND_STEPS,
  MAX_SPIN_MS,
  MAX_SPIN_STEPS,
  MIN_SPIN_MS,
  MIN_SPIN_STEPS,
  SETTLE_MS,
  randInt,
  type AnimMode,
} from './readyStep.constants';
import { slotSpinAnimation } from './slotSpinAnimation';
import { useTileStep } from './useTileStep';
import { CardSlotTile } from './CardSlotTile';
import { ReelErrorMessage } from './ReelErrorMessage';
import type { NextPackErrorCopy } from './nextPackPurchase';
import { stageReserves } from './stageLayout';
import { SleekButton, useIsSleek } from '../theme';
import { Button } from '../design-system/Button';
import { haptic } from '../haptics';

// Card-reel lane geometry (CARD_TILE_COUNT / CARD_CENTER_INDEX) lives in
// readyStep.constants — the centre is DERIVED from the count there so it
// always equals the strip's geometric middle (the tile `justify-center`
// rests under the viewport). A hand-set centre that isn't the middle lands
// the spin on the wrong tile while the real pull is pinned elsewhere.

export interface CardShuffleReelProps {
  /** Real pool-item card images to spin through (cycled to fill the lane). */
  images: string[];
  /** Current winner card — seeds the reel's starting centre tile so the
   *  shuffle visibly "starts" from the card the user just had. */
  currentImage: string | null;
  /** The card the wheel must land on (the next pull). */
  targetImage: string | null;
  /** Shared pedestal under the reel — same art as the winner scene. */
  pedestalSrc: string;
  /** Resolution of the parallel purchase. 'pending' while it's in flight (the
   *  reel spins + holds); 'success' → land on `targetImage`; 'failed' → abort
   *  to an empty pedestal and show the error. */
  outcome: 'pending' | 'success' | 'failed';
  /** Error copy for the failed state (null while pending/success). */
  error: NextPackErrorCopy | null;
  /** Fired once the wheel has landed and the neighbours have cleared,
   *  leaving only the landed tile. `skipped` tells the caller the player
   *  fast-forwarded — it can then cut its own post-landing choreography,
   *  mirroring the first open where Skip bypasses the glow reveal. */
  onLanded: (skipped: boolean) => void;
  /** The player's standing wish to skip: the spin fast-forwards the moment
   *  it can, as if Skip were tapped — and keeps doing so while it holds. */
  skip?: boolean;
  /** "Add funds" in the error state — set only for an insufficient-balance
   *  failure when the casino gave us a deposit_url. Rendered above Try Again. */
  onAddFunds?: (() => void) | undefined;
  /** "Try Again" in the error state — re-run the whole buy. Shown only when
   *  `error.canRetry` is true. */
  onRetry: () => void;
  /** "Back" in the error state — leave the shuffle (caller decides where to). */
  onBack: () => void;
  /** Tile look — `'card'` (default) spins pool-item cards on pedestals;
   *  `'pack'` spins the sealed pack art with no pedestal, mirroring the
   *  first-open ReadyStep lane. */
  tileVariant?: 'card' | 'pack';
  /** `true` while the caller plays its post-landing reveal choreography
   *  (white glow over the landed pack) — the landed tile crossfades out
   *  into the glow, like the first-open lane does in its `reveal` phase. */
  revealing?: boolean;
}

/**
 * Full-screen "Open Next Pack" shuffle reel. Takes over the whole
 * viewport (over the reveal background, like the initial open) and
 * reuses the EXACT slot mechanics of that first reveal — `slotSpinAnimation`
 * + the shared `readyStep` constants (same overshoot/rebound curve, spin
 * length, motion blur) — so the reveal feels identical.
 *
 * Each tile is a full card+pedestal mini-scene (every card on its own
 * plinth), sized to the winner scene's `320/500` proportions, so the
 * centred/landed tile matches the winner card exactly. The lane breaks
 * out to the FULL viewport width (`100vw`, centred on the scene) so the
 * cards slide across the whole screen during the spin.
 *
 * Auto-spins once on mount. Starts parked (`idle` → the real cards rest in
 * a row across the screen) and kicks the spin the moment `tileStep` is
 * measured, so the travel distance is always real. Beats mirror ReadyStep,
 * with a hold so the wheel never reveals before the pull is known:
 *   idle → spin → [hold until `outcome` resolves] →
 *     success: settle → cleared → onLanded
 *     failed:  settle → aborting (tiles fade) → errored (error panel + Retry/Back).
 */
export function CardShuffleReel({
  images,
  currentImage,
  targetImage,
  pedestalSrc,
  outcome,
  error,
  onLanded,
  skip: skipWanted = false,
  onAddFunds,
  onRetry,
  onBack,
  tileVariant = 'card',
  revealing = false,
}: CardShuffleReelProps) {
  // Same bands in the winner, the reel and the opener scene (see stageLayout).
  const isSleek = useIsSleek();
  const reserves = stageReserves(isSleek);
  const { ref: tileRef, tileStep } = useTileStep();
  const [mode, setMode] = useState<AnimMode>('idle');
  // Px correction for justify-center mis-centring the tile strip (iOS rests it
  // a constant ~15 tiles off — see slotSpinAnimation). Measured at spin-start
  // off the centre tile's real position so every landing is dead-centre.
  const [baseOffset, setBaseOffset] = useState(0);
  // Guards the one-shot residual snap at landing (see the cleared effect).
  const [snapDone, setSnapDone] = useState(false);
  // Lane element — used to locate the winning tile (by data-ti) for the
  // residual centring snap at landing.
  const laneEl = useRef<HTMLDivElement>(null);
  // The travel animation has finished; the reel now waits on `outcome`
  // before settling (so a slow purchase just holds the wheel a beat longer).
  const [spinDone, setSpinDone] = useState(false);
  // Player hit "Skip" — the lane decelerates straight to the landing and the
  // settle beat collapses to 0 (but the reel still HOLDS for `outcome`,
  // so it never reveals before the real pull is known).
  const [skipped, setSkipped] = useState(false);
  // Player hit "Skip" while the pull was still pending. We can't land yet (the
  // real card is unknown), so we flag the request and keep the reel MOVING,
  // then fast-forward the instant `outcome` resolves. Stopping on the
  // placeholder now and swapping its image when the backend answers is exactly
  // the "lands on a card, then a different card appears" bug.
  const [skipRequested, setSkipRequested] = useState(false);
  // Image pinned to the landing tile the moment spin finishes AND outcome is
  // known. Prevents the tile from swapping when the backend delivers
  // `targetImage` — the abrupt image change during hold would look like a bug.
  const [frozenLandedImage, setFrozenLandedImage] = useState<string | null>(null);
  // How many spin_hold extend batches the lane has travelled into: 0 before
  // any hold, 1 on the FIRST hold batch, then +1 per batch. It MUST count the
  // first batch (not stay 0) so slotSpinAnimation's rest position matches
  // where the lane actually scrolled — otherwise `cleared` jumps back.
  const [extendBatch, setExtendBatch] = useState(0);

  // Latest onLanded — kept in a ref so the hand-off timer isn't reset by
  // parent re-renders (which recreate the callback) while `cleared`.
  const onLandedRef = useRef(onLanded);
  useEffect(() => {
    onLandedRef.current = onLanded;
  }, [onLanded]);

  // Mirror of `skipped` for the hand-off timer below — keeps that effect's
  // deps on `mode` only (skipped can't change once the lane has cleared).
  const skippedRef = useRef(false);
  useEffect(() => {
    skippedRef.current = skipped;
  }, [skipped]);

  // Timestamp when the spin started — used to time the spin_hold intercept
  // (cut to a constant-speed hold before the visible braking tail begins).
  const spinStartTimeRef = useRef(0);

  useEffect(() => {
    if (mode === 'idle' && tileStep > 0) {
      spinStartTimeRef.current = Date.now();
      // Measure how far the centre tile actually sits from the viewport centre
      // at rest (justify-center is exact on desktop but ~15 tiles off on iOS).
      // Cancel that offset so the spin lands the target tile dead-centre.
      const cr = tileRef.current?.getBoundingClientRect();
      if (cr) {
        setBaseOffset(-(cr.left + cr.width / 2 - window.innerWidth / 2));
      }
      setMode('spin');
    }
  }, [mode, tileStep, tileRef]);

  // No auto-skip on purpose: when the backend resolves mid-spin we let the
  // spin play out its natural slot curve (accel → cruise → decel → overshoot
  // → rebound) instead of yanking the lane into a 460ms skip. That yank
  // RE-ACCELERATED the reel after it had already begun braking — nothing like
  // a real slot. The landing tile is pinned to the real pull, so the spin
  // simply decelerates onto it. (Manual "Skip" still fast-forwards on demand;
  // spin_hold below still covers a backend slower than the whole spin.)

  // Freeze the landing tile's image once the motion stops AND the outcome is
  // known. Guarding on outcome prevents freezing to a placeholder card during
  // spin_hold batches — we only want to freeze to the REAL pulled card.
  useEffect(() => {
    if (!spinDone || frozenLandedImage !== null || outcome === 'pending') return;
    setFrozenLandedImage(targetImage ?? null);
  }, [spinDone, frozenLandedImage, targetImage, outcome]);

  // One spin per mount — frozen on first render. (Replays remount the
  // reel with a fresh key, so a new spin length is rolled each time.)
  const [{ spinSteps, spinDuration }] = useState(() => ({
    spinSteps: randInt(MIN_SPIN_STEPS, MAX_SPIN_STEPS),
    // Stretched vs the ready lane — the card tiles are bigger, so the same
    // travel would whip past too fast to read without the scale-up.
    spinDuration:
      (MIN_SPIN_MS + Math.random() * (MAX_SPIN_MS - MIN_SPIN_MS)) *
      CARD_SPIN_MS_SCALE,
  }));

  const landedIndex = CARD_CENTER_INDEX + spinSteps;
  // The tile that ACTUALLY rests under the viewport centre. During a plain
  // spin that's landedIndex, but every spin_hold extend batch scrolls the lane
  // another EXTEND_STEPS further, so the centred tile moves with it. isLanded
  // (and the neighbour-clear) must follow this tile — pinning it to landedIndex
  // makes the centred card fade out on `cleared` while the "landed" tile sits
  // off-screen to the left.
  const landedCentreIdx = landedIndex + EXTEND_STEPS * extendBatch;

  // If the outcome is still pending when the spin reaches ~72% of its
  // duration, cut over to spin_hold BEFORE the SLOT_CURVE bezier starts
  // its visible braking tail — the reel keeps moving at constant speed
  // instead of decelerating and stopping in front of the user.
  useEffect(() => {
    if (mode !== 'spin' || spinDone || outcome !== 'pending') return;
    const interceptMs = spinDuration * 0.72;
    const elapsed = Date.now() - spinStartTimeRef.current;
    const wait = Math.max(0, interceptMs - elapsed);
    const t = window.setTimeout(() => {
      // First hold batch is batch #1 — commit it so the rest position tracks
      // where the lane scrolls to (no teleport back to finalX on cleared).
      setExtendBatch(1);
      setMode('spin_hold');
    }, wait);
    return () => window.clearTimeout(t);
  }, [mode, spinDone, outcome, spinDuration]);

  // After spin completes, use the frozen image so the landing tile is stable.
  // Before that, targetImage is live — the landing tile is pinned to it, so
  // the spin decelerates straight onto the real card with no visible swap.
  const effectiveTargetImage = frozenLandedImage ?? targetImage;

  // Fill the lane with real pool images (cycled with a stride so adjacent
  // tiles differ), pin the START centre to the current card, and pin the
  // LANDING tile + all extend-batch landing tiles to effectiveTargetImage
  // so the correct card appears regardless of how many batches were needed.
  const tiles = useMemo(() => {
    const pool =
      images.length > 0 ? images : currentImage ? [currentImage] : [];
    // Pick a stride that is coprime with pool.length so every tile in the
    // strip shows a distinct image — stride 7 silently collides when
    // pool.length is a multiple of 7 and makes all tiles identical.
    // We try a short list of primes; `i % pool.length` is the guaranteed
    // fallback (sequential, always covers every image).
    const stride =
      pool.length > 1
        ? ([11, 13, 17, 19, 23, 5, 3, 2, 1].find(
            (p) => pool.length % p !== 0,
          ) ?? 1)
        : 1;
    const arr: (string | null)[] = Array.from(
      { length: CARD_TILE_COUNT },
      (_, i) => (pool.length ? pool[(i * stride) % pool.length]! : null),
    );
    arr[CARD_CENTER_INDEX] = currentImage ?? arr[CARD_CENTER_INDEX] ?? null;
    arr[landedIndex] = effectiveTargetImage ?? arr[landedIndex] ?? null;
    // Mark every possible extend-batch landing tile with the target image so
    // the correct card is centred regardless of how many extend batches ran.
    for (let b = 1; b <= 5; b++) {
      const idx = landedIndex + EXTEND_STEPS * b;
      if (idx < CARD_TILE_COUNT) {
        arr[idx] = effectiveTargetImage ?? arr[idx] ?? null;
      }
    }
    return arr;
  }, [images, currentImage, effectiveTargetImage, landedIndex]);

  // Memoised so the motion targets keep a STABLE reference across the
  // re-renders that fire mid-spin (the purchase resolving updates
  // targetImage / outcome). A fresh object each render makes framer-motion
  // restart the keyframe spin — the wheel jumps back and re-spins.
  //
  // The lane animation is computed in TILE UNITS (tileStep = 1), so it never
  // depends on the pixel pitch. The pixel translate is derived live in the JSX
  // via `translateX(calc(var(--lane-tiles) * var(--step) * 1px + ...))`, where
  // `--step` is the LIVE measured pitch. A viewport resize (or an iOS toolbar
  // collapse that shrinks the tiles) only updates `--step` — it re-centres the
  // lane instantly without re-running the spin, so there's no restart, no
  // hitch, no reversal, and the landing stays on the winner. baseOffset is the
  // px centring correction; it's applied as a separate term in that same calc
  // (NOT baked into the tile-unit targets here, since it can't be expressed in
  // tile units that survive a resize).
  const { laneAnimate, laneTransition, blurAnimate, blurTransition } = useMemo(
    () =>
      slotSpinAnimation({
        animMode: mode,
        spinSteps,
        spinDuration,
        tileStep: 1,
        extendBatch,
        extendSteps: EXTEND_STEPS,
      }),
    [mode, spinSteps, spinDuration, extendBatch],
  );

  // The travel keyframes finished — mark it. Settling waits for readiness.
  // Fires for the natural spin end, a skip decel, and each extend batch.
  const handleLaneComplete = () => {
    if (mode === 'spin') {
      haptic('land'); // mechanical "plonk" buzz on the natural land
      setSpinDone(true);
    } else if (mode === 'skip' || mode === 'spin_hold') {
      setSpinDone(true);
    }
  };

  // Natural spin finished but outcome is still pending → start the extend
  // phase: keep scrolling at constant velocity so the reel never freezes.
  useEffect(() => {
    if (!spinDone || outcome !== 'pending' || mode !== 'spin') return;
    setSpinDone(false);
    // First hold batch is batch #1 — see the intercept effect above.
    setExtendBatch(1);
    setMode('spin_hold');
  }, [spinDone, outcome, mode]);

  // Each extend batch finished with outcome still pending → scroll another
  // batch further (up to the tile-strip runway).
  useEffect(() => {
    if (!spinDone || outcome !== 'pending' || mode !== 'spin_hold') return;
    const nextLandingIdx = landedIndex + EXTEND_STEPS * (extendBatch + 1);
    if (nextLandingIdx >= CARD_TILE_COUNT - 1) return; // out of runway
    setSpinDone(false);
    setExtendBatch((b) => b + 1);
  }, [spinDone, outcome, mode, extendBatch, landedIndex]);

  // Skip — fast-forward to the won card. If the pull is already known the lane
  // decelerates straight onto it (`skip` mode, see `slotSpinAnimation`); the
  // hand-off (settle → clear → onLanded) is the same path with a collapsed
  // settle beat. During spin_hold we bump extendBatch so the decel targets the
  // next (further-left) landing instead of snapping backward.
  //
  // If the pull is still PENDING we must NOT stop: a stop now freezes the reel
  // on the placeholder, then swaps to the real card when the backend answers
  // (the "wrong card" bug). Instead we flag the request and keep the reel
  // moving; the effect below decelerates the instant the outcome resolves.
  const skip = () => {
    if (mode !== 'spin' && mode !== 'spin_hold') return;
    setSkipped(true);
    if (outcome === 'pending') {
      setSkipRequested(true);
      return;
    }
    if (mode === 'spin_hold') {
      setExtendBatch((b) => b + 1);
    }
    setMode('skip');
  };

  // The player's standing skip (the top-bar switch) presses Skip for them
  // as soon as the reel is spinning
  useEffect(() => {
    if (!skipWanted) return;
    if (mode !== 'spin' && mode !== 'spin_hold') return;
    if (skipped) return;
    skip();
    // `skip` is a plain closure over the current render; the guards above
    // are the whole condition
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipWanted, mode, skipped]);

  // A skip queued while the pull was pending fires the moment the outcome
  // resolves — the reel decelerates from its still-moving position straight
  // onto the now-known real card (no stop on a placeholder, no image swap).
  useEffect(() => {
    if (!skipRequested || outcome === 'pending') return;
    if (mode !== 'spin' && mode !== 'spin_hold') return;
    if (mode === 'spin_hold') {
      setExtendBatch((b) => b + 1);
    }
    setMode('skip');
  }, [skipRequested, outcome, mode]);

  // Spin / skip / extend-batch finished AND the purchase resolved → settle on
  // the landed card (success) or begin the abort sequence (failed).
  useEffect(() => {
    if (
      !spinDone ||
      outcome === 'pending' ||
      (mode !== 'spin' && mode !== 'skip' && mode !== 'spin_hold')
    ) {
      return;
    }
    const t = window.setTimeout(
      () => setMode(outcome === 'success' ? 'cleared' : 'aborting'),
      skipped ? 0 : SETTLE_MS,
    );
    return () => window.clearTimeout(t);
  }, [spinDone, outcome, mode, skipped]);

  // One-shot residual snap. baseOffset (measured at rest) clears the whole
  // ~15-tile iOS centring error, but a fraction of a tile can survive the huge
  // translateX. The instant we land (cleared), measure the winning tile's
  // leftover offset and fold it into baseOffset ONCE — guarded so the resulting
  // re-render can't re-trigger it. Runs in a layout effect so the correction
  // applies before paint (no visible jump).
  useLayoutEffect(() => {
    if (mode !== 'cleared' || snapDone) return;
    setSnapDone(true);
    const el = laneEl.current?.querySelector(`[data-ti="${landedCentreIdx}"]`);
    if (!el) return;
    const r = el.getBoundingClientRect();
    const off = r.left + r.width / 2 - window.innerWidth / 2;
    if (Math.abs(off) > 1) setBaseOffset((b) => b - off);
  }, [mode, snapDone, landedCentreIdx]);

  // Neighbours faded → hand off to the next episode.
  useEffect(() => {
    if (mode !== 'cleared') return;
    const t = window.setTimeout(
      () => onLandedRef.current(skippedRef.current),
      CLEAR_TRANSITION_MS + 80,
    );
    return () => window.clearTimeout(t);
  }, [mode]);

  // Abort: tiles fade to an empty pedestal over CLEAR_TRANSITION_MS, then the
  // error panel + actions take the stage. No card is ever revealed.
  useEffect(() => {
    if (mode !== 'aborting') return;
    const t = window.setTimeout(() => setMode('errored'), CLEAR_TRANSITION_MS + 80);
    return () => window.clearTimeout(t);
  }, [mode]);

  return (
    <motion.div
      className="safe-area-pad fixed inset-0 z-40 flex flex-col items-center overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.3, ease: 'easeOut' } }}
      // Slightly delayed fade-in so the winner card visibly fades out
      // first and the sealed packs then bloom in over the cleared stage.
      // Pre-delay entrance: transition={{ duration: 0.3, ease: 'easeOut' }}
      transition={{ duration: 0.4, ease: 'easeOut', delay: 0.2 }}
    >
      {/* Top + bottom spacers reserve the SAME regions the winner uses for
          its title + bottom panel, so the reel's card area (the `grow`
          middle) lines up exactly with the winner card — no vertical jump
          on the reel→winner crossfade. */}
      <div
        className="w-full shrink-0"
        style={{ minHeight: reserves.top }}
        aria-hidden
      />

      <div className="relative grow min-h-0 w-full flex items-center justify-center">
        {/* Scene box — sized like the winner scene; only sets the reel
            HEIGHT. Each tile is a full card+pedestal mini-scene of these
            proportions, so the centred tile matches the winner card. */}
        <div
          className="relative"
          style={{
            aspectRatio: '320 / 500',
            height: 'min(56vh, 540px, 92vw * 500 / 320)',
            maxHeight: '100%',
          }}
        >
          {/* Reel band — full scene height, FULL viewport width so the cards
              slide across the whole screen. Centred on the scene.

              `overflow-x-clip` (NOT `overflow-hidden`): the card sits flush at
              the top of the scene box (`object-bottom`, matching
              WinnerContent), so `overflow-hidden` could shave its top edge
              during the spin (the landed winner card — no band — shows it in
              full). Clip only the horizontal axis (hide cards sliding past the
              viewport edges) and leave the vertical free, so the card top is
              never trimmed. */}
          <div
            className="absolute inset-y-0 overflow-x-clip"
            style={{ width: '100vw', left: '50%', transform: 'translateX(-50%)' }}
          >
            {/* `--step` (the LIVE tile pitch) + `--base-offset` (the px
                centring correction) live on this plain wrapper so they inherit
                down to the lane. The lane animates `--lane-tiles` (tile units)
                and maps to pixels via the calc transform below. A resize only
                updates `--step` here, re-centring the lane instantly with no
                re-animation (no restart/hitch, no reversal) — and the same path
                absorbs an iOS toolbar collapse that shrinks the tiles mid-spin.
                (`--*` keys stay off the motion.div: its MotionStyle type rejects
                them.) */}
            <div
              className="flex h-full w-full items-center justify-center"
              style={
                {
                  '--step': tileStep,
                  '--base-offset': `${baseOffset}px`,
                } as CSSProperties
              }
            >
              <motion.div
                ref={laneEl}
                className="h-full will-change-transform"
                style={{
                  transform:
                    'translateX(calc(var(--lane-tiles, 0) * var(--step, 0) * 1px + var(--base-offset, 0px)))',
                }}
                animate={{ '--lane-tiles': laneAnimate.x }}
                transition={laneTransition}
                onAnimationComplete={handleLaneComplete}
              >
                <motion.div
                  className="flex h-full items-stretch gap-10 @[500px]:gap-20"
                  animate={blurAnimate}
                  transition={blurTransition}
                >
                  {tiles.map((img, i) => (
                    <CardSlotTile
                      key={i}
                      ref={i === CARD_CENTER_INDEX ? tileRef : undefined}
                      index={i}
                      imageUrl={img}
                      pedestalSrc={pedestalSrc}
                      isLanded={i === landedCentreIdx}
                      mode={mode}
                      variant={tileVariant}
                      revealing={revealing}
                    />
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </div>
          {(mode === 'aborting' || mode === 'errored') && error && (
            <ReelErrorMessage
              headline={error.headline}
              body={error.body}
              pedestalSrc={pedestalSrc}
            />
          )}
        </div>
      </div>

      {/* Bottom region — same reserved height the winner uses for its panel,
          so the reel's card area lines up with the winner card. While the
          wheel spins it holds the "Skip" button (same primary CTA as
          Open Next Pack, no icon) so the player can fast-forward. */}
      <div
        className={
          // Sleek matches its winner band's side padding so Skip and the CTA
          // it replaces are the same width.
          isSleek
            ? 'w-full shrink-0 flex flex-col items-center justify-end px-5 pb-6'
            : 'w-full shrink-0 flex flex-col items-center justify-end px-6 pb-6'
        }
        style={{ minHeight: reserves.bottom }}
      >
        {(mode === 'spin' || mode === 'spin_hold') && (
          <motion.div
            className="w-full max-w-[480px] mx-auto"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut', delay: 0.2 }}
          >
            {isSleek ? (
              // The next-pack CTA the player just pressed, now fast-forwarding
              <SleekButton name="RevealNextPackButton" onClick={skip} className="w-full">
                Skip
              </SleekButton>
            ) : (
              <Button
                variant="primary"
                size="xl"
                onClick={skip}
                fullWidth
                className="h-16"
              >
                Skip
              </Button>
            )}
          </motion.div>
        )}
        {mode === 'errored' && error && (
          <motion.div
            className="w-full max-w-[480px] mx-auto flex flex-col items-center gap-3"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            {error.canRetry ? (
              <>
                {onAddFunds && (
                  <Button
                    variant="primary"
                    size="xl"
                    onClick={onAddFunds}
                    fullWidth
                    className="h-16"
                  >
                    Add funds
                  </Button>
                )}
                <Button
                  variant={onAddFunds ? 'secondary' : 'primary'}
                  size="xl"
                  onClick={onRetry}
                  fullWidth
                  className="h-16"
                >
                  Try Again
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onBack}
                  className="mx-auto !h-auto !px-0 underline underline-offset-4 type-subhead font-normal"
                >
                  Back
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                size="xl"
                onClick={onBack}
                fullWidth
                className="h-16"
              >
                Back
              </Button>
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
